import React, { useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import LogService from '../services/LogService';
import { LogStats, ExecutionLockStatus } from '../types/LogTypes';
import './Dashboard.css';

const REFRESH_INTERVAL_MS = 60000; // 60 seconds

function renderInline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|`(.+?)`/g;
  let last = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={idx++}>{m[1]}</strong>);
    else if (m[2] !== undefined) parts.push(<code key={idx++} className="rn-code">{m[2]}</code>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderMarkdown(md: string): ReactNode {
  const lines = md.split('\n');
  const elements: ReactNode[] = [];
  let listItems: string[] = [];
  let k = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={k++} className="rn-list">
          {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </ul>
      );
      listItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith('# ')) {
      flushList();
      elements.push(<h1 key={k++} className="rn-h1">{renderInline(line.slice(2))}</h1>);
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={k++} className="rn-h2">{renderInline(line.slice(3))}</h2>);
    } else if (line.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={k++} className="rn-h3">{renderInline(line.slice(4))}</h3>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      listItems.push(line.slice(2));
    } else if (line === '---') {
      flushList();
      elements.push(<hr key={k++} className="rn-hr" />);
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={k++} className="rn-p">{renderInline(line)}</p>);
    }
  }
  flushList();
  return elements;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secondsSinceRefresh, setSecondsSinceRefresh] = useState(0);
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState<Date | null>(null);
  const [triggeringRefresh, setTriggeringRefresh] = useState(false);
  const [executionLockStatus, setExecutionLockStatus] = useState<ExecutionLockStatus | null>(null);
  const [canTriggerRefresh, setCanTriggerRefresh] = useState(true);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [releaseNotesContent, setReleaseNotesContent] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/RELEASE_NOTES.md')
      .then(r => r.text())
      .then(setReleaseNotesContent)
      .catch(() => setReleaseNotesContent('# Release Notes\n\nUnable to load release notes.'));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowReleaseNotes(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LogService.getStats();
      setStats(data);
      setSecondsSinceRefresh(0); // Reset countdown on successful refresh
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkExecutionLock = useCallback(async () => {
    try {
      const lockStatus = await LogService.getExecutionLockStatus();
      setExecutionLockStatus(lockStatus);
      
      // Determine if button should be enabled
      if (!lockStatus.exists) {
        setCanTriggerRefresh(true);
      } else if (lockStatus.lockExpiryTimeUtc) {
        const expiryTime = new Date(lockStatus.lockExpiryTimeUtc);
        const now = new Date();
        setCanTriggerRefresh(expiryTime < now);
      } else {
        setCanTriggerRefresh(false);
      }
    } catch (err) {
      console.error('Failed to check execution lock:', err);
      // If we can't check the lock, allow the button to be enabled
      setCanTriggerRefresh(true);
    }
  }, []);

  // Set up auto-refresh and countdown timer
  useEffect(() => {
    // Initial load
    loadStats();
    checkExecutionLock();

    // Start countdown timer (updates every second)
    countdownTimerRef.current = setInterval(() => {
      setSecondsSinceRefresh(prev => prev + 1);
    }, 1000);

    // Start auto-refresh timer (refreshes every minute)
    refreshTimerRef.current = setInterval(() => {
      // Only refresh if the page is visible
      if (document.visibilityState === 'visible') {
        loadStats();
        checkExecutionLock();
      }
    }, REFRESH_INTERVAL_MS);

    // Handle visibility change - refresh when tab becomes visible if data is stale
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If tab becomes visible and data is older than 60 seconds, refresh immediately
        setSecondsSinceRefresh(currentSeconds => {
          if (currentSeconds >= 60) {
            loadStats();
            checkExecutionLock();
          }
          return currentSeconds;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadStats, checkExecutionLock]);

  const handleManualRefresh = () => {
    loadStats();
    checkExecutionLock();
  };

  const handleLoadData = async () => {
    try {
      setTriggeringRefresh(true);
      await LogService.triggerAggregation();
      setLastRefreshTrigger(new Date());
      
      // Immediately check lock status after triggering
      await checkExecutionLock();
    } catch (err) {
      console.error('Failed to trigger aggregation:', err);
      setError(err instanceof Error ? err.message : 'Failed to trigger data refresh');
    } finally {
      setTriggeringRefresh(false);
    }
  };

  const handleFilterClick = (filterType: 'logType' | 'environment' | 'customer' | 'service', value: string) => {
    // Navigate to logs page with filter parameter
    navigate(`/logs?${filterType}=${encodeURIComponent(value)}`);
  };

  const handleViewAllLogs = () => {
    navigate('/logs');
  };

  const sortEntriesAlphabetically = (entries: Record<string, number>): [string, number][] => {
    return Object.entries(entries).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const formatNumber = (num?: number) => {
    return (num ?? 0).toLocaleString();
  };

  const getLastRefreshTriggerTime = (): string => {
    // If execution lock has a start time, use that (regardless of exists flag)
    if (executionLockStatus?.lockStartTimeUtc) {
      return formatDate(executionLockStatus.lockStartTimeUtc);
    }
    
    // Otherwise use the local trigger time if available
    if (lastRefreshTrigger) {
      return formatDate(lastRefreshTrigger.toISOString());
    }
    
    return 'Never';
  };

  const isLastRefreshRecent = (): boolean => {
    const tenMinutesInMs = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    // Check execution lock start time first
    if (executionLockStatus?.lockStartTimeUtc) {
      const lockStartTime = new Date(executionLockStatus.lockStartTimeUtc);
      const now = new Date();
      const timeDiff = now.getTime() - lockStartTime.getTime();
      return timeDiff < tenMinutesInMs;
    }
    
    // Otherwise check local trigger time
    if (lastRefreshTrigger) {
      const now = new Date();
      const timeDiff = now.getTime() - lastRefreshTrigger.getTime();
      return timeDiff < tenMinutesInMs;
    }
    
    return false;
  };

  if (loading && !stats) {
    return <div className="dashboard-loading">Loading statistics...</div>;
  }

  if (error && !stats) {
    return (
      <div className="dashboard-error">
        <p>Error: {error}</p>
        <button onClick={handleManualRefresh}>Retry</button>
      </div>
    );
  }

  if (!stats) {
    return <div className="dashboard-error">No statistics available</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title-row">
          <h1>Log Dashboard</h1>
          <button className="release-notes-btn" onClick={() => setShowReleaseNotes(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Release Notes
          </button>
        </div>
        <div className="refresh-info">
          <span className="refresh-timer">
            Last refreshed: {secondsSinceRefresh}s ago
          </span>
        </div>
      </div>
      
      <div className="stats-overview">
        <div className="stat-card clickable" onClick={handleViewAllLogs} title="Click to view all logs">
          <h3>Total Logs</h3>
          <p className="stat-value">{formatNumber(stats.totalLogs)}</p>
        </div>
        <div className="stat-card">
          <h3>Newest Log</h3>
          <p className="stat-value">{formatDate(stats.newestLog)}</p>
        </div>
        <div className="stat-card refresh-trigger-card">
          <h3>Last Data Refresh Triggered</h3>
          <p className="stat-value">{getLastRefreshTriggerTime()}</p>
          {executionLockStatus?.exists ? (
            <div className="lock-message">
              Currently Processing
            </div>
          ) : isLastRefreshRecent() ? (
            <div className="lock-message">
              Recently refreshed
            </div>
          ) : (
            <button 
              onClick={handleLoadData} 
              className="load-data-button"
              disabled={triggeringRefresh}
            >
              {triggeringRefresh ? 'Loading...' : 'Load Data'}
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-section">
          <h2>Logs by Type</h2>
          <div className="stat-list">
            {stats.logsByType && Object.keys(stats.logsByType).length > 0 ? (
              sortEntriesAlphabetically(stats.logsByType).map(([type, count]) => (
                <div 
                  key={type} 
                  className="stat-item clickable"
                  onClick={() => handleFilterClick('logType', type)}
                  title={`Click to view ${type} logs`}
                >
                  <span className="stat-label">{type}</span>
                  <span className="stat-count">{formatNumber(count)}</span>
                </div>
              ))
            ) : (
              <div className="no-data">No data available</div>
            )}
          </div>
        </div>

        <div className="stat-section">
          <h2>Logs by Environment</h2>
          <div className="stat-list">
            {stats.logsByEnvironment && Object.keys(stats.logsByEnvironment).length > 0 ? (
              sortEntriesAlphabetically(stats.logsByEnvironment).map(([env, count]) => (
                <div 
                  key={env} 
                  className="stat-item clickable"
                  onClick={() => handleFilterClick('environment', env)}
                  title={`Click to view logs from ${env} environment`}
                >
                  <span className="stat-label">{env}</span>
                  <span className="stat-count">{formatNumber(count)}</span>
                </div>
              ))
            ) : (
              <div className="no-data">No data available</div>
            )}
          </div>
        </div>

        <div className="stat-section">
          <h2>Logs by Customer</h2>
          <div className="stat-list">
            {stats.logsByCustomer && Object.keys(stats.logsByCustomer).length > 0 ? (
              sortEntriesAlphabetically(stats.logsByCustomer).map(([customer, count]) => (
                <div 
                  key={customer} 
                  className="stat-item clickable"
                  onClick={() => handleFilterClick('customer', customer)}
                  title={`Click to view logs for ${customer}`}
                >
                  <span className="stat-label">{customer}</span>
                  <span className="stat-count">{formatNumber(count)}</span>
                </div>
              ))
            ) : (
              <div className="no-data">No data available</div>
            )}
          </div>
        </div>

        <div className="stat-section">
          <h2>Logs by Service</h2>
          <div className="stat-list">
            {stats.logsByService && Object.keys(stats.logsByService).length > 0 ? (
              sortEntriesAlphabetically(stats.logsByService).map(([service, count]) => (
                <div 
                  key={service} 
                  className="stat-item clickable"
                  onClick={() => handleFilterClick('service', service)}
                  title={`Click to view logs for ${service}`}
                >
                  <span className="stat-label">{service}</span>
                  <span className="stat-count">{formatNumber(count)}</span>
                </div>
              ))
            ) : (
              <div className="no-data">No data available</div>
            )}
          </div>
        </div>
      </div>

      <div className="refresh-section">
        <button onClick={handleManualRefresh} className="refresh-button" disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {showReleaseNotes && (
        <div className="modal-overlay" onClick={() => setShowReleaseNotes(false)}>
          <div className="modal rn-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Release Notes</h2>
              <button className="modal-close" onClick={() => setShowReleaseNotes(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="rn-content">
                {renderMarkdown(releaseNotesContent)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
